import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { useTheme } from 'next-themes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChartExportButton from "@/components/shared/ChartExportButton";
import { useIsMobile } from '@/hooks/use-mobile';

interface GeographicData {
  area_code: string;
  area_name: string;
  state_abbr: string;
  a_median?: number;
  employment?: number;
  growth_5yr?: number | null;
  latitude?: number;
  longitude?: number;
}

interface GeographicHeatmapProps {
  data: GeographicData[];
  metric: 'salary' | 'employment' | 'growth';
  onMetricChange: (metric: 'salary' | 'employment' | 'growth') => void;
  title?: string;
  occupationTitle?: string;
  onStateClick?: (stateAbbr: string) => void;
  /** When true, skip the outer Card wrapper (for embedding inside another Card). */
  embedded?: boolean;
  /**
   * When false, hide the export button and metric select in the header
   * (for embeds whose parent already renders its own controls).
   */
  showControls?: boolean;
}

export const GeographicHeatmap: React.FC<GeographicHeatmapProps> = ({
  data,
  metric,
  onMetricChange,
  title = "Geographic Trends Analysis",
  occupationTitle = "Selected Occupation",
  onStateClick,
  embedded = false,
  showControls = true,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const isMobile = useIsMobile();

  const { tickValues, tickText } = useMemo(() => {
    if (!data || data.length === 0) {
      return { tickValues: [], tickText: [] };
    }

    let values: number[] = [];
    let min = 0;
    let max = 0;

    switch (metric) {
      case 'salary':
        values = data.map(d => d.a_median || 0).filter(v => v > 0);
        min = Math.min(...values);
        max = Math.max(...values);
        break;
      case 'employment':
        values = data.map(d => d.employment || 0).filter(v => v > 0);
        min = Math.min(...values);
        max = Math.max(...values);
        break;
      case 'growth':
        values = data.map(d => d.growth_5yr || 0);
        min = Math.min(...values);
        max = Math.max(...values);
        break;
    }

    if (values.length === 0 || min === max) {
      return { tickValues: [], tickText: [] };
    }

    // Calculate 3 ticks (min, middle, max) for better spacing
    const range = max - min;
    const middle = min + range / 2;
    const ticks = [min, middle, max].map(v => {
      if (metric === 'growth') {
        return parseFloat(v.toFixed(3));
      }
      return Math.round(v);
    });

    // Format tick labels compactly
    const formatTick = (value: number): string => {
      if (metric === 'growth') {
        return (value * 100).toFixed(1) + '%';
      }

      if (metric === 'salary') {
        if (value >= 1_000_000) {
          return '$' + (value / 1_000_000).toFixed(1) + 'M';
        } else if (value >= 1_000) {
          return '$' + (value / 1_000).toFixed(0) + 'K';
        }
        return '$' + Math.round(value).toLocaleString();
      }

      // employment
      if (value >= 1_000_000) {
        return (value / 1_000_000).toFixed(1) + 'M';
      } else if (value >= 1_000) {
        return (value / 1_000).toFixed(0) + 'K';
      }
      return Math.round(value).toLocaleString();
    };

    return {
      tickValues: ticks,
      tickText: ticks.map(formatTick)
    };
  }, [data, metric]);

  // Hardcoded hex for Plotly geo fills — CSS-var HSL (even comma-form) has been
  // unreliable for layout.geo.bgcolor, which falls back to Plotly's default #fff.
  // Values match src/index.css :root / .dark tokens.
  const themeColors = useMemo(
    () =>
      isDark
        ? {
            mapBg: '#241f1e', // --card 25 7% 13%
            landColor: '#2b2624', // --muted 25 7% 16%
            lakeColor: '#241f1e',
            borderColor: '#36302e', // --border 25 6% 20%
            textColor: '#d5dbe3', // --foreground 210 17% 87%
          }
        : {
            mapBg: '#ffffff',
            landColor: '#e5e7eb',
            lakeColor: '#ffffff',
            // Visible slate — theme --border is near-white and hides AK/HI insets.
            borderColor: '#94a3b8',
            textColor: '#1e2430', // --foreground 220 13% 13%
          },
    [isDark]
  );

  const { chartData, colorScale, metricInfo } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        chartData: [],
        colorScale: [],
        metricInfo: { title: '', format: '', min: 0, max: 0 }
      };
    }

    let values: number[] = [];
    let metricInfo = { title: '', format: '', min: 0, max: 0 };

    switch (metric) {
      case 'salary':
        values = data.map(d => d.a_median || 0).filter(v => v > 0);
        metricInfo = {
          title: 'Median Annual Salary',
          format: '$,.0f',
          min: Math.min(...values),
          max: Math.max(...values)
        };
        break;
      case 'employment':
        values = data.map(d => d.employment || 0).filter(v => v > 0);
        metricInfo = {
          title: 'Total Employment',
          format: ',.0f',
          min: Math.min(...values),
          max: Math.max(...values)
        };
        break;
      case 'growth':
        values = data.map(d => d.growth_5yr || 0);
        metricInfo = {
          title: '5-Year Growth Rate',
          format: '.1%',
          min: Math.min(...values),
          max: Math.max(...values)
        };
        break;
    }

    // Single shared trace so there is only one colorbar
    const locations = data.map(d => d.state_abbr);
    const zValues = data.map(d => {
      switch (metric) {
        case 'salary':
          return d.a_median || 0;
        case 'employment':
          return d.employment || 0;
        case 'growth':
          return d.growth_5yr || 0;
        default:
          return 0;
      }
    });

    const textValues = data.map(d => {
      let value = 0;
      switch (metric) {
        case 'salary':
          value = d.a_median || 0;
          break;
        case 'employment':
          value = d.employment || 0;
          break;
        case 'growth':
          value = d.growth_5yr || 0;
          break;
      }

      return (
        `${d.area_name}<br>` +
        `${metricInfo.title}: ${value.toLocaleString(undefined, {
          style: metric === 'salary' ? 'currency' : metric === 'growth' ? 'percent' : 'decimal',
          currency: 'USD',
          minimumFractionDigits: metric === 'growth' ? 1 : 0,
          maximumFractionDigits: metric === 'growth' ? 1 : 0
        })}`
      );
    });

    const chartData = [
      {
        type: 'choropleth' as const,
        locationmode: 'USA-states',
        locations,
        z: zValues,
        text: textValues,
        hovertemplate: '%{text}<extra></extra>',
        coloraxis: 'coloraxis',
        zmid: metric === 'growth' ? 0 : undefined,
        marker: {
          line: {
            color: themeColors.borderColor,
            width: 0.5
          }
        }
      }
    ];

    return {
      chartData,
      colorScale: values,
      metricInfo
    };
  }, [data, metric, themeColors.borderColor]);

  const layout = useMemo(() => {
    const { mapBg, landColor, lakeColor, borderColor, textColor } = themeColors;

    // On mobile the card header already names the chart; the Plotly title just
    // truncates and collides with the modebar, so drop it. The colorbar moves
    // below the map horizontally so the map can use the full card width.
    const colorbar = isMobile
      ? {
          title: {
            text: metricInfo.title,
            side: 'top' as const,
            font: { size: 10, color: textColor }
          },
          orientation: 'h' as const,
          x: 0.5,
          xanchor: 'center' as const,
          y: -0.02,
          yanchor: 'top' as const,
          len: 0.9,
          thickness: 12
        }
      : {
          title: {
            text: metricInfo.title,
            side: 'right' as const,
            font: { size: 10, color: textColor }
          },
          x: 1.08,
          xanchor: 'left' as const,
          len: 0.8,
          thickness: 20
        };

    return {
      ...(isMobile
        ? {}
        : {
            title: {
              text: `${occupationTitle} - ${metricInfo.title} by State`,
              font: { size: 16, color: textColor }
            }
          }),
      geo: {
        scope: 'usa',
        projection: { type: 'albers usa' },
        showland: true,
        showocean: true,
        showlakes: true,
        bgcolor: mapBg,
        landcolor: landColor,
        oceancolor: mapBg,
        lakecolor: lakeColor,
        subunitcolor: borderColor,
        countrycolor: borderColor,
        coastlinecolor: borderColor
      },
      coloraxis: {
        colorscale:
          metric === 'growth'
            ? [
                [0, '#ef4444'],
                [0.5, isDark ? '#374151' : '#cbd5e1'],
                [1, '#22c55e']
              ]
            : [
                // Light floor must stay above white so low-end / AK-HI insets remain visible
                [0, isDark ? '#232120' : '#c7d2fe'],
                [0.5, isDark ? '#2d6a4f' : '#6366f1'],
                [1, isDark ? '#0ea5e9' : '#1e40af']
              ],
        colorbar: {
          ...colorbar,
          ticklabelposition: 'outside',
          ...(tickValues.length > 0 && tickText.length > 0
            ? {
                tickmode: 'array' as const,
                tickvals: tickValues,
                ticktext: tickText
              }
            : {
                tickmode: 'auto' as const,
                nticks: 3,
                tickformat: metricInfo.format
              }),
          tickfont: {
            size: 8,
            color: textColor
          },
          outlinecolor: borderColor,
          bgcolor: 'rgba(0,0,0,0)',
          xpad: 4,
          ypad: 4
        }
      },
      margin: isMobile
        ? { l: 0, r: 0, t: 10, b: 60 }
        : { l: 0, r: 120, t: 60, b: 0 },
      // Match geo bgcolor so the chart frame isn't Plotly's default white paper.
      paper_bgcolor: mapBg,
      plot_bgcolor: mapBg,
      font: { family: 'Inter, sans-serif', color: textColor }
    };
  }, [isDark, isMobile, metric, metricInfo, occupationTitle, themeColors, tickText, tickValues]);

  // Modebar is desktop-only (overlaps the map / awkward on touch).
  // Geo modebar defaults: camera, pan, zoom+/-, Reset (resetGeo), hover toggle.
  // Do not put string names like 'resetViews' in modeBarButtonsToAdd — Plotly
  // silently drops them for geo plots, which hid the reset control earlier.
  const config = {
    displayModeBar: !isMobile,
    displaylogo: false,
    responsive: true,
    scrollZoom: !isMobile,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'zoom2d', 'autoScale2d'],
  };

  if (!data || data.length === 0) {
    const emptyContent = (
      <div className="text-center text-muted-foreground py-8">
        No geographic data available for this selection
      </div>
    );

    if (embedded) return emptyContent;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>{emptyContent}</CardContent>
      </Card>
    );
  }

  // Top and bottom performers
  const sortedData = [...data].sort((a, b) => {
    const aVal =
      metric === 'salary'
        ? a.a_median || 0
        : metric === 'employment'
        ? a.employment || 0
        : a.growth_5yr || 0;
    const bVal =
      metric === 'salary'
        ? b.a_median || 0
        : metric === 'employment'
        ? b.employment || 0
        : b.growth_5yr || 0;
    return bVal - aVal;
  });

  const topPerformers = sortedData.slice(0, 3);
  const bottomPerformers = sortedData.slice(-3).reverse();

  const headerBlock = (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <CardTitle className="text-lg">{title}</CardTitle>
      {showControls && (
        <div className="flex items-center gap-2">
          {/* Export is desktop-only: it truncates on narrow cards and the
              modebar it relies on is hidden on mobile anyway */}
          <ChartExportButton
            chartId="geographic-heatmap-chart"
            filename={`geographic-heatmap-${occupationTitle?.replace(/[^a-z0-9]/gi, '_') || 'chart'}`}
            className="hidden sm:inline-flex"
          />
          <Select
            value={metric}
            onValueChange={(value: 'salary' | 'employment' | 'growth') =>
              onMetricChange(value)
            }
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="salary">Median Salary</SelectItem>
              <SelectItem value="employment">Employment</SelectItem>
              <SelectItem value="growth">Growth Rate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  const descriptionBlock = (
    <CardDescription>
      Interactive map showing {metricInfo.title.toLowerCase()} across {data.length} regions
    </CardDescription>
  );

  const bodyContent = (
    <div className="space-y-4">
      {/* Interactive Map */}
      <div
        id="geographic-heatmap-chart"
        className="h-72 sm:h-96 overflow-hidden rounded-md"
        style={{ backgroundColor: themeColors.mapBg }}
      >
        <Plot
          // isMobile is part of the key because react-plotly.js only applies
          // `config` (modebar visibility) at initialization, not on update
          key={`${resolvedTheme ?? 'light'}-${metric}-${isMobile ? 'mobile' : 'desktop'}`}
          data={chartData as unknown}
          layout={layout as unknown}
          config={config}
          style={{ width: '100%', height: '100%', backgroundColor: themeColors.mapBg }}
          useResizeHandler={true}
          onInitialized={(figure, graphDiv) => {
            if (graphDiv && onStateClick) {
              graphDiv.on('plotly_click', (event: unknown) => {
                if (event?.points?.[0]?.location) {
                  const stateAbbr = event.points[0].location;
                  onStateClick(stateAbbr);
                }
              });
            }
          }}
          onUpdate={(figure, graphDiv) => {
            if (graphDiv && onStateClick) {
              graphDiv.removeAllListeners('plotly_click');
              graphDiv.on('plotly_click', (event: unknown) => {
                if (event?.points?.[0]?.location) {
                  const stateAbbr = event.points[0].location;
                  onStateClick(stateAbbr);
                }
              });
            }
          }}
        />
      </div>

      {/* Top & Bottom Performers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <h4 className="font-medium text-success">Top Performers</h4>
          {topPerformers.map(area => {
            const value =
              metric === 'salary'
                ? area.a_median || 0
                : metric === 'employment'
                ? area.employment || 0
                : area.growth_5yr || 0;
            return (
              <div key={area.area_code} className="flex justify-between text-sm">
                <span>{area.area_name}</span>
                <span className="font-medium">
                  {value.toLocaleString(undefined, {
                    style:
                      metric === 'salary'
                        ? 'currency'
                        : metric === 'growth'
                        ? 'percent'
                        : 'decimal',
                    currency: 'USD',
                    minimumFractionDigits: metric === 'growth' ? 1 : 0,
                    maximumFractionDigits: metric === 'growth' ? 1 : 0
                  })}
                </span>
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <h4 className="font-medium text-destructive">Bottom Performers</h4>
          {bottomPerformers.map(area => {
            const value =
              metric === 'salary'
                ? area.a_median || 0
                : metric === 'employment'
                ? area.employment || 0
                : area.growth_5yr || 0;
            return (
              <div key={area.area_code} className="flex justify-between text-sm">
                <span>{area.area_name}</span>
                <span className="font-medium">
                  {value.toLocaleString(undefined, {
                    style:
                      metric === 'salary'
                        ? 'currency'
                        : metric === 'growth'
                        ? 'percent'
                        : 'decimal',
                    currency: 'USD',
                    minimumFractionDigits: metric === 'growth' ? 1 : 0,
                    maximumFractionDigits: metric === 'growth' ? 1 : 0
                  })}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="flex flex-wrap justify-between items-center gap-2 text-sm text-muted-foreground border-t pt-4">
        <div>
          Range: {metricInfo.min.toLocaleString()} - {metricInfo.max.toLocaleString()}
        </div>
        <Badge variant="outline">{data.length} regions analyzed</Badge>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          {headerBlock}
          {descriptionBlock}
        </div>
        {bodyContent}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        {headerBlock}
        {descriptionBlock}
      </CardHeader>
      <CardContent>{bodyContent}</CardContent>
    </Card>
  );
};
